import {
    Box,
    Checkbox,
    FormControl,
    FormHelperText,
    FormControlLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';

import { getAsPhoneNumber } from 'src/utils/get-as-phone-number';
import { defaultOption } from 'src/features/details-form/constants';

import { PhoneInput } from '../phone-input';
import { DateOfBirthInput } from '../date-of-birth-input';
import { PasswordField } from '../password-field';

import { type ControlledFieldProps, FieldTypes, TextFieldTypes } from './types';
import { styles } from './styles';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ControlledField: FC<ControlledFieldProps<any>> = ({
    name,
    control,
    label,
    placeholder,
    type,
    restStyles,
    options,
    disabled = false,
    fieldType = FieldTypes.Input,
    required = true,
    fullWidth = true,
}) => {
    const { t } = useTranslation();
    const { register } = useFormContext();

    const getFirstOption = (value: string) => value || defaultOption.value;

    const labelComponent =
        fieldType === FieldTypes.Input || fieldType === FieldTypes.Password ? (
            <Typography variant="subtitle4" css={disabled && styles.labelDisabled}>
                {label}
            </Typography>
        ) : (
            <Box css={styles.label}>
                <Typography variant="subtitle4" css={disabled && styles.labelDisabled}>
                    {label}
                </Typography>
                {required && (
                    <Typography variant="body2" css={styles.asterisk}>
                        *
                    </Typography>
                )}
            </Box>
        );

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState }) => {
                const error = !!fieldState.error;
                const helperText = t(fieldState.error?.message ?? '');

                const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
                    const { value } = event.target;

                    switch (type) {
                        case TextFieldTypes.EMAIL:
                            field.onChange(value.toLowerCase());
                            break;
                        case TextFieldTypes.POSTCODE:
                            field.onChange(value.toUpperCase());
                            break;
                        default:
                            field.onChange(value);
                    }

                    field.onBlur();
                };

                const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                    field.onChange(event.target.value);
                };

                switch (fieldType) {
                    case FieldTypes.Select:
                        return (
                            <FormControl css={styles.selectWrapper}>
                                {labelComponent}
                                <Select
                                    {...register(name)}
                                    labelId={name}
                                    {...field}
                                    variant="standard"
                                    fullWidth={fullWidth}
                                    required={required}
                                    error={error}
                                    disabled={disabled}
                                    value={getFirstOption(field.value)}
                                    css={styles.selectText}
                                >
                                    {options?.map((option) => {
                                        const isObject = typeof option === 'object';
                                        const optionValue = isObject ? option.value : option;
                                        const optionLabel = isObject ? option.label : option;

                                        return (
                                            <MenuItem key={optionValue} value={optionValue}>
                                                <Typography noWrap css={styles.dropdownWrap}>
                                                    {t(optionLabel)}
                                                </Typography>
                                            </MenuItem>
                                        );
                                    })}
                                </Select>

                                {error && (
                                    <FormHelperText variant="standard" error>
                                        {t(helperText)}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    case FieldTypes.Phone:
                        return (
                            <PhoneInput
                                {...register(name)}
                                value={field.value || getAsPhoneNumber('')}
                                onChange={field.onChange}
                                label={labelComponent}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                            />
                        );
                    case FieldTypes.DateOfBirth:
                        return (
                            <DateOfBirthInput
                                {...register(name)}
                                value={field.value || ''}
                                label={labelComponent}
                                onChange={field.onChange}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                            />
                        );
                    case FieldTypes.Password:
                        return (
                            <PasswordField
                                {...field}
                                {...register(name)}
                                ref={field.ref}
                                value={field.value}
                                onChange={field.onChange}
                                variant="standard"
                                labelComponent={labelComponent}
                                placeholder={placeholder}
                                fullWidth={fullWidth}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                            />
                        );
                    case FieldTypes.Checkbox:
                        return (
                            <FormControl error={error}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                            disabled={disabled}
                                            css={styles.formConfirmationText}
                                        />
                                    }
                                    label={<Typography variant="body3">{label}</Typography>}
                                />

                                {error && (
                                    <FormHelperText css={styles.confirmationError}>
                                        {t(helperText)}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        );
                    default:
                        return (
                            <TextField
                                {...field}
                                {...register(name)}
                                variant="standard"
                                type={type}
                                label={labelComponent}
                                placeholder={placeholder}
                                InputLabelProps={{ shrink: true }}
                                css={css(restStyles)}
                                fullWidth={fullWidth}
                                required={required}
                                error={error}
                                helperText={helperText}
                                disabled={disabled}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        );
                }
            }}
        />
    );
};
